import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileUp, Loader2 } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { getUploadLimit, saveUploadLimit } from '../../lib/settingsApi';
import { bytesToMb, setUploadLimitBytes } from '../../lib/fileUpload';
import { UPLOAD_LIMIT_KEY } from '../../hooks/useUploadLimit';

/**
 * Settings → Integrations, under the Google Drive card: the largest single
 * file anyone may upload. The storage proxy enforces it on every upload into
 * Drive (project, client, supplier, person and company resources, task and
 * lead attachments, meeting notes); the browser checks it first so a big file
 * is turned away before it starts moving.
 */
export function UploadLimitCard() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const query = useQuery({ queryKey: UPLOAD_LIMIT_KEY, queryFn: getUploadLimit });
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (query.data) setValue(String(bytesToMb(query.data.maxUploadBytes)));
  }, [query.data]);

  const capMb = query.data ? bytesToMb(query.data.hardCapBytes) : null;
  const minMb = query.data ? bytesToMb(query.data.minBytes) : 1;
  const currentMb = query.data ? bytesToMb(query.data.maxUploadBytes) : null;
  const parsed = Number(value.replace(',', '.'));
  const valid =
    value.trim() !== '' &&
    Number.isFinite(parsed) &&
    capMb !== null &&
    parsed >= minMb &&
    parsed <= capMb;
  const dirty = currentMb !== null && valid && parsed !== currentMb;

  async function save(mb: number | null) {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const next = await saveUploadLimit(mb);
      qc.setQueryData(UPLOAD_LIMIT_KEY, next);
      setUploadLimitBytes(next.maxUploadBytes);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="rounded-lg border border-gray-200 bg-card p-5">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-brand-600/15 p-2 text-brand-600">
          <FileUp className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{t('settings.uploadLimit.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('settings.uploadLimit.description')}</p>
        </div>
      </div>

      {query.isError && (
        <p className="mt-4 text-sm text-red-600">{t('settings.uploadLimit.loadError')}</p>
      )}

      {query.data && (
        <form
          className="mt-4 flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (dirty) void save(parsed);
          }}
        >
          <label className="flex flex-col gap-1 text-xs font-medium text-gray-600">
            {t('settings.uploadLimit.label')}
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="decimal"
                min={minMb}
                max={capMb ?? undefined}
                step={1}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setSaved(false);
                }}
                className="w-28 rounded-md border border-gray-300 bg-card px-3 py-2 text-sm text-gray-900 focus:border-brand-500 focus:outline-none"
              />
              <span className="text-sm text-gray-500">MB</span>
            </div>
          </label>
          <button
            type="submit"
            disabled={!dirty || saving}
            className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('settings.uploadLimit.save')}
          </button>
          {currentMb !== capMb && (
            <button
              type="button"
              onClick={() => void save(null)}
              disabled={saving}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
            >
              {t('settings.uploadLimit.reset', { cap: capMb ?? '' })}
            </button>
          )}
        </form>
      )}

      {/* One line: grey as a hint, red while the typed value is outside it. */}
      {query.data && (
        <p
          className={`mt-2 text-xs ${value.trim() !== '' && !valid ? 'text-red-600' : 'text-gray-500'}`}
        >
          {t('settings.uploadLimit.range', { min: minMb, cap: capMb ?? '' })}
        </p>
      )}
      {saved && <p className="mt-2 text-xs text-green-600">{t('settings.uploadLimit.saved')}</p>}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </section>
  );
}
